import { Checkbox, Group, Stack, Text } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'

import { ActionButton, AppModal } from '@/components/ui'
import type { MacroRow, ProjectTemplateRow } from '@/types/api'

type TicketReplyToolsModalsProps = {
  varsOpened: boolean
  varsLoading: boolean
  varsMap: Record<string, string>
  onCloseVars: () => void
  favoritesOpened: boolean
  favoritesSaving: boolean
  templates: ProjectTemplateRow[]
  macros: MacroRow[]
  favoriteTemplateIds: string[]
  favoriteMacroIds: string[]
  onCloseFavorites: () => void
  onSaveFavorites: () => void
  onToggleTemplate: (id: string) => void
  onToggleMacro: (id: string) => void
}

export function TicketReplyToolsModals({
  varsOpened,
  varsLoading,
  varsMap,
  onCloseVars,
  favoritesOpened,
  favoritesSaving,
  templates,
  macros,
  favoriteTemplateIds,
  favoriteMacroIds,
  onCloseFavorites,
  onSaveFavorites,
  onToggleTemplate,
  onToggleMacro,
}: TicketReplyToolsModalsProps) {
  return (
    <>
      <AppModal
        opened={varsOpened}
        onClose={onCloseVars}
        title="Переменные шаблонов"
        footer={<ActionButton action="cancel" label="Закрыть" onClick={onCloseVars} />}
      >
        {varsLoading ? (
          <Text size="sm" c="dimmed">
            Загрузка…
          </Text>
        ) : (
          <Stack gap="xs">
            <Text size="xs" c="dimmed" component="div">
              В тексте сообщения используйте формат [имя_переменной] — значения подставятся при отправке (для
              сообщений оператора). В поле ответа наберите «/» для быстрого выбора избранных или первых шаблонов и
              макросов.
            </Text>
            <Stack gap={6} style={{ maxHeight: 360, overflowY: 'auto' }}>
              {Object.keys(varsMap).length === 0 ? (
                <Text size="xs" c="dimmed">
                  Список пуст или не удалось загрузить.
                </Text>
              ) : (
                Object.entries(varsMap).map(([key, description]) => (
                  <Group key={key} align="flex-start" wrap="nowrap" gap="xs">
                    <Text size="xs" ff="monospace" style={{ flexShrink: 0 }}>
                      [{key}]
                    </Text>
                    <Text size="xs" c="dimmed">
                      {description}
                    </Text>
                  </Group>
                ))
              )}
            </Stack>
          </Stack>
        )}
      </AppModal>

      <AppModal
        opened={favoritesOpened}
        onClose={onCloseFavorites}
        title="Частые шаблоны и макросы"
        footer={
          <>
            <ActionButton action="cancel" onClick={onCloseFavorites} disabled={favoritesSaving} />
            <ActionButton
              action="save"
              loading={favoritesSaving}
              onClick={() =>
                openConfirmModal({
                  title: 'Сохранить избранное?',
                  children: (
                    <Text size="sm" c="dimmed">
                      Список частых шаблонов и макросов для этого проекта будет обновлён.
                    </Text>
                  ),
                  labels: { confirm: 'Сохранить', cancel: 'Отмена' },
                  onConfirm: onSaveFavorites,
                })
              }
            />
          </>
        }
      >
        <Stack gap="md">
          <Text size="xs" c="dimmed">
            Не более 5 шаблонов и 5 макросов — они появятся кнопками над полем ответа.
          </Text>
          <div>
            <Text fw={600} size="xs" mb={6}>
              Шаблоны
            </Text>
            <Stack gap={6} style={{ maxHeight: 200, overflowY: 'auto' }}>
              {templates.length === 0 ? (
                <Text size="xs" c="dimmed">
                  Нет шаблонов в проекте
                </Text>
              ) : (
                templates.map((template) => (
                  <Checkbox
                    key={template.id}
                    size="xs"
                    label={template.name}
                    checked={favoriteTemplateIds.includes(template.id)}
                    onChange={() => onToggleTemplate(template.id)}
                  />
                ))
              )}
            </Stack>
          </div>
          <div>
            <Text fw={600} size="xs" mb={6}>
              Макросы
            </Text>
            <Stack gap={6} style={{ maxHeight: 200, overflowY: 'auto' }}>
              {macros.length === 0 ? (
                <Text size="xs" c="dimmed">
                  Нет макросов
                </Text>
              ) : (
                macros.map((macro) => (
                  <Checkbox
                    key={macro.id}
                    size="xs"
                    label={macro.name}
                    checked={favoriteMacroIds.includes(macro.id)}
                    onChange={() => onToggleMacro(macro.id)}
                  />
                ))
              )}
            </Stack>
          </div>
        </Stack>
      </AppModal>
    </>
  )
}
